import { PropsWithChildren, ReactNode, useMemo } from "react";

import Link from "@whoppah/next/link";
import { useRouter } from "next/router";

import { cn } from "@/common/utils/css.utils";
import { decodeHtmlEntities } from "@/common/utils/html-entities.utils";
import { useProductProBuyerPrice } from "@/features/pro-buyer-price/hooks/useProBuyerPrice";
import { PROMOTION_FRAME_CLASS } from "@/features/promotions/components/PromotionCardAppearance";
import { PromotionDisclosure } from "@/features/promotions/components/PromotionDisclosure";
import { PROMOTIONS_ENABLED } from "@/features/promotions/config";
import { usePromotionObservation } from "@/features/promotions/hooks/usePromotionObservation";
import { ProductCardBadges } from "@/features/search/components/product-card/ProductCardBadges";
import { ProductCardFavorite } from "@/features/search/components/product-card/ProductCardFavorite";
import { ProductCardImage } from "@/features/search/components/product-card/ProductCardImage";
import { ProductCardLabels } from "@/features/search/components/product-card/ProductCardLabels";
import { ProductCardPrice } from "@/features/search/components/product-card/ProductCardPrice";
import { ProductCardSkeleton } from "@/features/search/components/product-card/ProductCardSkeleton";
import { ProductCardTitle } from "@/features/search/components/product-card/ProductCardTitle";
import {
  ProductCardContextProvider,
  type ProductCardContextValue,
  type ProductCardProduct,
} from "@/features/search/components/product-card/product-card.context";
import { formatAmount } from "@/features/search/utils/product.utils";
import { ProductVatPriceLine } from "@/features/seller-invoice/components/VatPriceLine";

type ProductCardProps = PropsWithChildren<{
  product: ProductCardProduct;
  /** Preload the image for above-the-fold cards (LCP). */
  priority?: boolean;
  className?: string;
  onSelect?: (product: ProductCardProduct) => void;
  /** Link somewhere other than the regular PDP (an Open Home piece links to its own page). */
  href?: string;
  /** A label over the image's bottom-left corner, e.g. "Open Home". */
  label?: ReactNode;
  /** Smaller default layout for dense rows: one-line title and a smaller price. */
  compact?: boolean;
  /**
   * Show the "Sponsored" line (advertiser + reason popover) under a promoted card. Design keeps it to
   * the paid homepage row (`HomepagePromotions`); search and category cards mark paid ranking with
   * the "Boosted" label instead (see `ProductCardLabels`).
   */
  showDisclosure?: boolean;
}>;

/**
 * Composed, linked product card built from `ProductCard.*` parts. Pass no
 * children for the default layout, or compose the parts yourself for a variant:
 *
 *   <ProductCard product={p}>
 *     <ProductCard.Image />
 *     <ProductCard.Title />
 *   </ProductCard>
 */
const ProductCardRoot = ({
  product,
  priority,
  className,
  onSelect,
  href,
  label,
  compact = false,
  showDisclosure = false,
  children,
}: ProductCardProps) => {
  const { locale } = useRouter();
  const promotion = PROMOTIONS_ENABLED ? product.promotion : null;
  const observation = usePromotionObservation(promotion?.token);
  const { price: proBuyerPrice } = useProductProBuyerPrice(product.id);

  const value = useMemo<ProductCardContextValue>(
    () => ({
      /* Titles can arrive HTML-escaped from the index or the stored listing ("B&amp;B"); decode
       * once here so the title and the image alt both show the plain text (WHO-5687). */
      product: { ...product, title: decodeHtmlEntities(product.title) },
      formatAmount: (amount: number | null | undefined) =>
        formatAmount(amount, product.currency, locale),
      formatListingAmount: (amount: number | null | undefined) =>
        product.listingPrices
          ? formatAmount(amount, product.listingPrices.currency, locale)
          : null,
      href: href ?? `/products/${product.slug}`,
      proBuyerPrice,
    }),
    [product, locale, proBuyerPrice, href]
  );

  return (
    <ProductCardContextProvider value={value}>
      <div
        ref={observation.ref}
        className={cn(
          promotion ? "min-w-0" : "contents",
          /* The frame pads the card by 9px; pulling it back out by as much keeps the image and text
           * aligned with their unframed neighbours, so the grid does not shift. */
          promotion?.highlight && [PROMOTION_FRAME_CLASS, "-m-[9px]"]
        )}
        data-promoted={promotion ? "true" : undefined}
      >
        <Link
          sourceLocale="en"
          href={value.href}
          prefetch={false}
          className={cn(
            "group flex flex-col",
            compact ? "space-y-2" : "space-y-4",
            className
          )}
          onClick={event => {
            if ((event.target as HTMLElement).closest("button")) {
              return;
            }

            observation.trackClick();
            onSelect?.(product);
          }}
        >
          {children ?? (
            <>
              <div className="relative">
                <ProductCardImage priority={priority} />
                <ProductCardBadges />
                <ProductCardFavorite />
                {label ? (
                  <div className="pointer-events-none absolute bottom-2 left-2">
                    {label}
                  </div>
                ) : null}
              </div>
              <div className="flex flex-col space-y-1">
                <ProductCardTitle
                  className={cn(compact && "line-clamp-1 md:text-sm")}
                />
                <ProductCardPrice
                  className={cn(
                    compact &&
                      "[&_.product-card-price]:text-sm md:[&_.product-card-price]:text-base"
                  )}
                />
                <ProductVatPriceLine productId={product.id} place="list" />
              </div>
              <ProductCardLabels />
            </>
          )}
        </Link>
        {promotion && showDisclosure && (
          <PromotionDisclosure disclosure={promotion.disclosure} />
        )}
      </div>
    </ProductCardContextProvider>
  );
};

export const ProductCard = Object.assign(ProductCardRoot, {
  Image: ProductCardImage,
  Title: ProductCardTitle,
  Price: ProductCardPrice,
  Badges: ProductCardBadges,
  Favorite: ProductCardFavorite,
  Labels: ProductCardLabels,
  Skeleton: ProductCardSkeleton,
});
